import React from 'react';
import { TableProps } from '@amzn/awsui-components-console/table';
import Link from '@amzn/awsui-components-console/link';

import { ActiveTranslationJob, HistoricalTranslationJob } from '../types';
import {
  formatEpochMillisDate,
  computeTurnaround,
} from 'src/common/utils/dateFormatters';
import {
  TranslationStatusIndicator,
  getTranslationStatusLabel,
} from 'src/common/translationStatus';
import { buildNamespacePath, buildStringGroupPath } from 'src/common/routes';
import type { TranslateFn } from 'src/i18n/shim/useTranslation';

/** String group cell link; SPA-navigates to the group detail page, matching the Namespace column. */
const StringGroupLink: React.FC<{
  item: { namespaceId: string; stringGroupId: string };
  navigate: (path: string) => void;
}> = ({ item, navigate }) => {
  const href = buildStringGroupPath(item.namespaceId, item.stringGroupId);
  return (
    <Link
      href={href}
      onFollow={(e) => {
        e.preventDefault();
        navigate(href);
      }}
    >
      {item.stringGroupId}
    </Link>
  );
};

/** Cells hold "V12", so size to the header rather than an equal share of spare width. */
const VERSION_COLUMN_WIDTH = 110;

/** Fits the header plus its sort control: narrowing Version truncated it to "Ingestion So…". */
const INGESTION_SOURCE_COLUMN_WIDTH = 190;

/*
 * Sorting comparators are module-level constants because Cloudscape decides
 * which header is sorted by comparator IDENTITY — a comparator rebuilt per
 * render would never match the active sorting column.
 */

/** Missing timestamps compare as 0, clustering NA rows at one end of the sort. */
const epochMillis = (value: string | number | null | undefined): number =>
  value ? Number(value) : 0;

const compareVersion = (
  a: { stringGroupVersion?: string | null },
  b: { stringGroupVersion?: string | null },
): number =>
  Number(a.stringGroupVersion ?? 0) - Number(b.stringGroupVersion ?? 0);

export const compareSentForTranslation = (
  a: { sentForTranslationDate?: string | null },
  b: { sentForTranslationDate?: string | null },
): number =>
  epochMillis(a.sentForTranslationDate) - epochMillis(b.sentForTranslationDate);

const compareDueDate = (
  a: { dueDate?: string | null },
  b: { dueDate?: string | null },
): number => epochMillis(a.dueDate) - epochMillis(b.dueDate);

const compareDeliveredOn = (
  a: { deliveredOnDate?: string | null },
  b: { deliveredOnDate?: string | null },
): number => epochMillis(a.deliveredOnDate) - epochMillis(b.deliveredOnDate);

/** Turnaround in raw millis — never the formatted "N days" cell text. */
const turnaroundMillis = (job: {
  sentForTranslationDate?: string | null;
  deliveredOnDate?: string | null;
}): number =>
  job.deliveredOnDate
    ? epochMillis(job.deliveredOnDate) - epochMillis(job.sentForTranslationDate)
    : 0;

const compareTurnaround = (
  a: Parameters<typeof turnaroundMillis>[0],
  b: Parameters<typeof turnaroundMillis>[0],
): number => turnaroundMillis(a) - turnaroundMillis(b);

/** Orders by the VISIBLE label, not the raw token, so sorting matches the column. */
/** Compares by the LOCALIZED label, not the contract value, so the ordering matches the text on
 * screen. `localeCompare` without a locale uses the runtime default, which is the right collation. */
const compareStatus =
  (t: TranslateFn) =>
  (
    a: { translationStatus?: string | null },
    b: { translationStatus?: string | null },
  ): number =>
    getTranslationStatusLabel(t, a.translationStatus ?? '').localeCompare(
      getTranslationStatusLabel(t, b.translationStatus ?? ''),
    );

/** Oldest sent first, for BOTH tables: the longest-waiting job is the closest to
 *  missing its due date, so it belongs on top rather than the last page. */
export const OLDEST_FIRST_SORTING_STATE = {
  sortingColumn: { sortingComparator: compareSentForTranslation },
  isDescending: false,
};

/**
 * Creates active job column definitions with SPA-aware links.
 * Uses onFollow + preventDefault to avoid full page reloads and
 * Midway/Cognito re-auth round-trips.
 */
export const createActiveColumnDefinitions = (
  t: TranslateFn,
  locale: string,
  navigate: (path: string) => void,
): TableProps.ColumnDefinition<ActiveTranslationJob>[] => [
  {
    id: 'stringGroup',
    header: t('track-column-string-group'),
    sortingField: 'stringGroupId',
    cell: (item) => <StringGroupLink item={item} navigate={navigate} />,
  },
  {
    id: 'version',
    header: t('track-column-version'),
    // Cells are short ("V12"), so the column only needs to fit its own header.
    width: VERSION_COLUMN_WIDTH,
    sortingComparator: compareVersion,
    cell: (item) => `V${item.stringGroupVersion}`,
  },
  {
    id: 'dueDate',
    header: t('track-column-due-date'),
    sortingComparator: compareDueDate,
    cell: (item) => formatEpochMillisDate(t, locale, item.dueDate),
  },
  {
    id: 'sentForTranslation',
    header: t('track-column-sent-on'),
    sortingComparator: compareSentForTranslation,
    cell: (item) =>
      formatEpochMillisDate(t, locale, item.sentForTranslationDate),
  },
  {
    id: 'status',
    header: t('track-column-status'),
    cell: (item) => (
      <TranslationStatusIndicator status={item.translationStatus} />
    ),
  },
  {
    id: 'trackingId',
    header: t('track-column-trace-id'),
    cell: (item) => item.trackingId,
  },
  {
    id: 'clientId',
    header: t('track-column-ingestion-source'),
    width: INGESTION_SOURCE_COLUMN_WIDTH,
    sortingField: 'clientId',
    cell: (item) => item.clientId,
  },
  {
    id: 'namespace',
    header: t('track-column-namespace'),
    cell: (item) => (
      <Link
        href={buildNamespacePath(item.namespaceId)}
        onFollow={(e) => {
          e.preventDefault();
          navigate(buildNamespacePath(item.namespaceId));
        }}
      >
        {item.namespaceId}
      </Link>
    ),
  },
];

/**
 * Creates historical job column definitions with SPA-aware links.
 */
export const createHistoricalColumnDefinitions = (
  t: TranslateFn,
  locale: string,
  navigate: (path: string) => void,
): TableProps.ColumnDefinition<HistoricalTranslationJob>[] => [
  {
    id: 'stringGroup',
    header: t('track-column-string-group'),
    sortingField: 'stringGroupId',
    cell: (item) => <StringGroupLink item={item} navigate={navigate} />,
  },
  {
    id: 'version',
    header: t('track-column-version'),
    width: VERSION_COLUMN_WIDTH,
    sortingComparator: compareVersion,
    cell: (item) => `V${item.stringGroupVersion}`,
  },
  {
    id: 'deliveredOn',
    header: t('track-column-delivered-on'),
    sortingComparator: compareDeliveredOn,
    /*
     * 'NA' is keyed off the absence of `deliveredOnDate`, not `status ===
     * CANCELLED`: the backend sets that date only for COMPLETE jobs, so absence
     * covers cancelled and failed jobs alike.
     */
    cell: (item) => formatEpochMillisDate(t, locale, item.deliveredOnDate),
  },
  {
    id: 'sentOn',
    header: t('track-column-sent-on'),
    sortingComparator: compareSentForTranslation,
    cell: (item) =>
      formatEpochMillisDate(t, locale, item.sentForTranslationDate),
  },
  {
    id: 'turnaround',
    header: t('track-column-tat'),
    sortingComparator: compareTurnaround,
    cell: (item) =>
      computeTurnaround(t, item.sentForTranslationDate, item.deliveredOnDate),
  },
  // Replaces the 'Job Failure' / 'Cancelled Job' Yes-No pair: a job has exactly one
  // terminal state, so it reads as one value, via the badge the active table uses.
  {
    id: 'status',
    header: t('track-column-status'),
    sortingComparator: compareStatus(t),
    cell: (item) => (
      <TranslationStatusIndicator status={item.translationStatus} />
    ),
  },
  {
    id: 'trackingId',
    header: t('track-column-trace-id'),
    cell: (item) => item.trackingId,
  },
  {
    id: 'clientId',
    header: t('track-column-ingestion-source'),
    width: INGESTION_SOURCE_COLUMN_WIDTH,
    sortingField: 'clientId',
    cell: (item) => item.clientId,
  },
  {
    id: 'namespace',
    header: t('track-column-namespace'),
    cell: (item) => (
      <Link
        href={buildNamespacePath(item.namespaceId)}
        onFollow={(e) => {
          e.preventDefault();
          navigate(buildNamespacePath(item.namespaceId));
        }}
      >
        {item.namespaceId}
      </Link>
    ),
  },
];
